
import React, { useState } from "react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { addDays, format, isSameDay } from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { fetchTasks } from "@/services/task/taskService";
import { fetchActivities } from "@/services/activity/activityService";
import { Skeleton } from "@/components/ui/skeleton";
import { TaskWithRelations, ActivityWithRelations } from "@/types/custom-types";

export function CalendarView() {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const navigate = useNavigate();

  // Fetch tasks and activities for the current date range
  const startDate = date ? new Date(date) : new Date();
  startDate.setHours(0, 0, 0, 0);
  const endDate = addDays(startDate, 30);

  const { data: tasks, isLoading: isLoadingTasks, error: tasksError } = useQuery({
    queryKey: [
      "tasks",
      {
        start: startDate.toISOString(),
        end: endDate.toISOString()
      }
    ],
    queryFn: () => fetchTasks()
  });

  const { data: activities, isLoading: isLoadingActivities, error: activitiesError } = useQuery({
    queryKey: [
      "activities",
      {
        start: startDate.toISOString(),
        end: endDate.toISOString()
      }
    ],
    queryFn: () => fetchActivities()
  });

  const handleAddTask = () => {
    navigate("/tasks/create", {
      state: {
        date: date?.toISOString()
      }
    });
  };

  const handleAddActivity = () => {
    navigate("/activities/create", {
      state: {
        date: date?.toISOString()
      }
    });
  };

  // Type guard functions to check type of event
  const isTaskWithRelations = (event: any): event is TaskWithRelations => {
    return event && 'due_date' in event && !('type' in event);
  };
  
  const isActivityWithRelations = (event: any): event is ActivityWithRelations => {
    return event && 'type' in event && !('due_date' in event);
  };

  // Combine tasks and activities for display
  const eventsForDate = (date: Date) => {
    const tasksForDate = tasks 
      ? tasks.filter((task) => task.due_date && isSameDay(new Date(task.due_date), date)) 
      : [];
    const activitiesForDate = activities 
      ? activities.filter((activity) => activity.date && isSameDay(new Date(activity.date), date)) 
      : [];
    return [...tasksForDate, ...activitiesForDate];
  };

  // Format events for the selected date
  const selectedDateEvents = date ? eventsForDate(date) : [];
  const isLoading = isLoadingTasks || isLoadingActivities;
  const error = tasksError || activitiesError;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="md:col-span-1">
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>Calendar</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddTask}
              >
                <PlusCircle className="h-4 w-4 mr-1" /> Task
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleAddActivity}
              >
                <PlusCircle className="h-4 w-4 mr-1" /> Activity
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CalendarComponent
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-md border"
          />
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>
            {date ? format(date, "MMMM d, yyyy") : "Select a date"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : error ? (
            <p className="text-sm text-muted-foreground">
              Failed to load events
            </p>
          ) : selectedDateEvents.length > 0 ? (
            <div className="space-y-3">
              {selectedDateEvents.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-3 border rounded-md"
                >
                  <div>
                    <p className="font-medium">{event.title}</p>
                    <p className="text-sm text-muted-foreground">
                      {isActivityWithRelations(event) ? event.type : "Task"}
                      {event.organization?.name && ` • ${event.organization.name}`}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/${isTaskWithRelations(event) ? "tasks" : "activities"}/${event.id}`)}
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-8 text-muted-foreground">
              No events scheduled for this date
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
